import { QueryKey, queryOptions, useQuery, UseQueryOptions, useInfiniteQuery, UseInfiniteQueryOptions, InfiniteData } from '@tanstack/react-query'

import { HttpError } from '@/api/http'
import { getNextPageParamHelper } from '../utils'

import {
  getPaymentCardGateway,
  getPaymentCreditCards,
  getPaymentCreditCard,
  getPaymentCredits,
  getPaymentHistory,
  getPaymentProducts,
} from './services'

import {
  GetPaymentCreditCardsPayload,
  GetPaymentCreditCardsResponse,
  GetPaymentCreditCardPayload,
  GetPaymentCreditCardResponse,
  GetPaymentCreditsResponse,
  GetPaymentHistoryResponse,
  GetPaymentProductPayload,
  GetPaymentProductResponse,
  PaymentHistoryFilters,
} from './types'

export const useQueryPaymentCardGateway = () =>
  useQuery({
    queryKey: ['payment-card-gateway'],
    queryFn: getPaymentCardGateway,
  })

export const createPaymentCreditsQueryOptions = () =>
  queryOptions({
    queryKey: ['payment-credits'] as const,
    queryFn: () => getPaymentCredits(),
  })

export const createPaymentHistoryQueryOptions = () =>
  queryOptions({
    queryKey: ['payment-history'] as const,
    queryFn: () => getPaymentHistory(),
  })

export const createPaymentCreditCardsQueryOptions = (payload: GetPaymentCreditCardsPayload) =>
  queryOptions({
    queryKey: ['payment-credit-cards', payload] as const,
    queryFn: () => getPaymentCreditCards(payload),
  })

export const useQueryPaymentCreditCards = (
  payload: GetPaymentCreditCardsPayload,
  options?: Omit<UseQueryOptions<GetPaymentCreditCardsResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['payment-credit-cards', payload]
  const queryFn = () => getPaymentCreditCards(payload)

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useQueryPaymentCreditCard = (
  payload: GetPaymentCreditCardPayload,
  options?: Omit<UseQueryOptions<GetPaymentCreditCardResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['payment-credit-card', payload.id]
  const queryFn = () => getPaymentCreditCard(payload)

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useQueryPaymentCredits = (
  options?: Omit<UseQueryOptions<GetPaymentCreditsResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['payment-credits']
  const queryFn = () => getPaymentCredits()

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useQueryPaymentHistory = (
  filters: PaymentHistoryFilters = {},
  options?: Omit<UseQueryOptions<GetPaymentHistoryResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['payment-history', filters]
  const queryFn = () => getPaymentHistory(filters)

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}

export const useInfiniteQueryPaymentHistory = (
  filters: PaymentHistoryFilters = {},
  options?: Omit<UseInfiniteQueryOptions<GetPaymentHistoryResponse, HttpError, InfiniteData<GetPaymentHistoryResponse>, QueryKey, number>, 'queryKey' | 'queryFn'>
) => {
  return useInfiniteQuery({
    queryKey: ['payment-history-infinite', filters],
    queryFn: ({ pageParam = 1 }) => getPaymentHistory({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: getNextPageParamHelper,
    ...options,
  })
}

export const useQueryPaymentProducts = (
  payload: GetPaymentProductPayload,
  options?: Omit<UseQueryOptions<GetPaymentProductResponse, HttpError>, 'queryKey' | 'queryFn'>
) => {
  const queryKey: QueryKey = ['payment-products', payload]
  const queryFn = () => getPaymentProducts(payload)

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  })
}
